import React from 'react';
import { motion } from 'framer-motion';
import { evidenceVault } from '../data/mockData';
import SafeIcon from '../common/SafeIcon';
import { EvidenceVaultIllustration } from '../components/VisualIllustrations';
import { FiUploadCloud, FiFileText, FiMoreVertical, FiCheck, FiX, FiClock, FiSearch, FiFilter } from 'react-icons/fi';

const EvidenceVault = () => {
  const getStatusIcon = (status) => {
    switch(status) {
      case 'Verified': return <SafeIcon icon={FiCheck} className="text-emerald-400" />;
      case 'Rejected': return <SafeIcon icon={FiX} className="text-red-400" />;
      case 'Pending Review': return <SafeIcon icon={FiClock} className="text-amber-400" />;
      default: return null;
    }
  };

  return (
    <div className="space-y-8 max-w-7xl mx-auto pb-10">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div>
          <div className="inline-flex items-center gap-2 bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-widest mb-2 border border-blue-500/20">
            Immutable Storage
          </div>
          <h1 className="text-3xl font-black text-slate-100 tracking-tight">Evidence Vault</h1>
          <p className="text-slate-400 mt-2 max-w-xl">Securely store, version, and link documentation to specific framework controls for a frictionless audit trail.</p>
        </div>
        <div className="flex gap-2">
          <button className="bg-slate-800 hover:bg-slate-700 text-slate-200 px-4 py-2.5 rounded-xl border border-slate-700 transition-all text-sm font-bold flex items-center gap-2">
            <SafeIcon icon={FiFilter} /> Filters
          </button>
          <button className="bg-amber-500 hover:bg-amber-400 text-slate-950 px-6 py-2.5 rounded-xl font-bold transition-all text-sm flex items-center gap-2 shadow-lg shadow-amber-500/20">
            <SafeIcon icon={FiUploadCloud} /> Upload Evidence
          </button>
        </div>
      </div>

      <EvidenceVaultIllustration />

      <div className="bg-slate-900/40 border border-slate-800/50 rounded-2xl overflow-hidden backdrop-blur-md shadow-2xl">
        <div className="p-4 border-b border-slate-800/50 bg-slate-950/30 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Recent Uploads</span>
            <span className="bg-slate-800 text-slate-400 text-[10px] px-2 py-0.5 rounded-full font-bold">24 Total</span>
          </div>
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" />
            <input type="text" placeholder="Filter by name..." className="bg-slate-950/50 border border-slate-800 rounded-lg pl-9 pr-4 py-1.5 text-xs text-slate-300 w-64 focus:outline-none focus:border-amber-500/30" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-950/40 border-b border-slate-800/50 text-[10px] uppercase tracking-widest text-slate-500">
                <th className="p-5 font-bold">Document Name</th>
                <th className="p-5 font-bold">Uploaded By</th>
                <th className="p-5 font-bold">Date</th>
                <th className="p-5 font-bold">Size</th>
                <th className="p-5 font-bold">Status</th>
                <th className="p-5 font-bold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {evidenceVault.map((item, i) => (
                <motion.tr 
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  key={item.id} 
                  className="hover:bg-slate-800/30 transition-colors group"
                >
                  <td className="p-5">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-slate-400 group-hover:bg-amber-500/10 group-hover:text-amber-500 transition-all border border-transparent group-hover:border-amber-500/20 shadow-inner">
                        <SafeIcon icon={FiFileText} className="text-xl" />
                      </div>
                      <div>
                        <p className="font-bold text-slate-200 text-sm group-hover:text-amber-400 transition-colors">{item.name}</p>
                        <p className="text-[10px] text-slate-500 mt-0.5 font-mono uppercase">{item.type}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-5">
                    <div className="flex items-center gap-2">
                       <div className="w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center text-[10px] font-bold text-slate-400">
                         {item.uploadedBy.charAt(0)}
                       </div>
                       <span className="text-sm text-slate-400">{item.uploadedBy}</span>
                    </div>
                  </td>
                  <td className="p-5 text-sm text-slate-500 font-medium">{item.date}</td>
                  <td className="p-5 text-sm text-slate-500 font-mono">{item.size}</td>
                  <td className="p-5">
                    <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest bg-slate-950/50 w-fit px-3 py-1.5 rounded-full border border-slate-800">
                      {getStatusIcon(item.status)}
                      <span className="text-slate-300">{item.status}</span>
                    </div>
                  </td>
                  <td className="p-5 text-right">
                    <button className="p-2 text-slate-600 hover:text-amber-500 transition-colors hover:bg-slate-800/80 rounded-lg">
                      <SafeIcon icon={FiMoreVertical} />
                    </button>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EvidenceVault;